import { MetapropertiesOption, SearchMetaproperty } from '../../../types';
import { removeSingleTextFilter, resetSimilarityFilters } from '../legacy/singleTextFilter';
import { emitSearchAssetsEvent } from './emitSearchAssetsEvent';
import type { SearchParams } from './types';

export const addSearchResultToFilterBar = (field: string, value: string, label?: string) => {
	const onFilterRemovedCallback = () => {
		window.bynder?.search?.FilterBar?.remove?.(field, value);
		window.bynder?.search?.FilterHelper?.search?.('media', {
			field,
			value,
			filterType: 'remove',
		});
	};

	window.bynder?.search?.FilterBar?.add?.(
		'',
		field,
		label || value, // label
		value, // value
		onFilterRemovedCallback,
		true,
	);
};

export const isReactAssetOverviewActive = () => !!document.querySelector('#rootAssetOverviewV3');

export const executeSearch = (
	params: SearchParams,
	metapropertyOptionLabel?: string,
	isPerformingNLSSearch?: boolean,
) => {
	const reactAssetOverviewIsActive = isReactAssetOverviewActive();
	const isInPimView = document.location.pathname.includes('/show/');

	// Don't remove this because it breaks the dashboard spotlight.
	// We don't have the asset overview there and so we need to redirect to the assets overview
	if (reactAssetOverviewIsActive && !isInPimView) {
		emitSearchAssetsEvent(params);
		addSearchResultToFilterBar(params.field, params.value, metapropertyOptionLabel);
	} else if (isPerformingNLSSearch) {
		const searchParams = new URLSearchParams(Object.entries(params));
		searchParams.delete('field');
		searchParams.delete('value');
		searchParams.delete('filterType');
		searchParams.delete('filterKey');
		const setFiltersRequestUrl = `/media/?${searchParams.toString()}`;
		document.location.href = setFiltersRequestUrl;
	} else {
		const searchParams = new URLSearchParams(Object.entries(params));
		const setFiltersRequestUrl = `/search/set/?${searchParams.toString()}`;
		document.location.href = setFiltersRequestUrl;
	}
};

export const getSearchParamsField = (
	isStickySearch: boolean,
	isMPOSearch?: boolean,
	metapropertyName?: string,
) => {
	if (isMPOSearch) {
		return `metaproperty_${metapropertyName}`;
	}

	if (isStickySearch) {
		return 'singletext';
	}

	return 'text';
};

export const resetStickySearchInAssetbank = (isNLSReset: boolean) => {
	if (isNLSReset) {
		resetSimilarityFilters();
	} else {
		removeSingleTextFilter();
	}
};

export const onMetapropertyOptionEnter = (
	metapropertyOption: MetapropertiesOption,
	defaultSearchAction: (
		isMPOSearch?: boolean,
		metapropertyName?: string,
		metapropertyOptionName?: string,
		metapropertyOptionLabel?: string,
	) => void,
	clearSearchTerm: () => void,
) => {
	if (metapropertyOption.spotlightFilterAction === 'pim') {
		document.location.href = `/show/${metapropertyOption.metapropertyName}/${metapropertyOption.id}/`;
	} else {
		defaultSearchAction(
			true,
			metapropertyOption.metapropertyName,
			metapropertyOption.name,
			metapropertyOption.label,
		);
		clearSearchTerm();
	}
};

const emitTagSearchEvent = (tagName: string) => {
	const searchAssetsEvent = new CustomEvent('react-overview_search-assets', {
		detail: {
			filters: { field: 'tags', value: tagName, filterType: 'add' }, // Only manual tags are displayed in spotlight results, no autotags
		},
	});

	window.dispatchEvent(searchAssetsEvent);
};

export const onTagSearch = (
	tagName: string | number,
	clearSearchTerm: () => void,
	e?: React.MouseEvent,
) => {
	e?.preventDefault();

	const reactAssetOverviewIsActive = isReactAssetOverviewActive();

	if (reactAssetOverviewIsActive) {
		emitTagSearchEvent(tagName.toString());
	} else {
		document.location.href = `/search/set/?field=tags&value=${encodeURIComponent(
			tagName,
		)}&filterType=add`;
	}

	clearSearchTerm();
};

export const getTagsAsSerchItemsFromSearchResults = (tags: (string | number)[]) => {
	// tags come as an array with label and count ["tag1", 4, "tag2", 7], we only want the string values
	// We need to build them into an object to be consistent with other search items
	return tags
		.filter((tag): tag is string => typeof tag === 'string')
		.map((item) => ({
			id: `tag_${item}`,
			name: item,
		}));
};

export const getMetapropertyOptionsFromSearchResults = (metaproperties: SearchMetaproperty[]) => {
	return metaproperties.flatMap((metaproperty) =>
		metaproperty.options?.map((option) => ({
			...option,
			metapropertyName: metaproperty.name,
			isMetapropertyOption: true,
		})),
	);
};
